import { QUESTION_TYPES, VW_DISPLAY_ORDERS } from '@api/instant_survey';

export const availableQuestionVariables = [
  'banner',
  'brand',
  'category',
  'channel',
  'date',
  'majorcat',
  'parentbrand',
  'parentchannel',
  'quota_group_name',
  'retailer',
];

export const newItemAvailableQuestionVariables = ['item_name'];

export const QUESTION_TYPES_HELP_URL =
  'https://help.numerator.com/en/articles/10949983-in-platform-surveys-question-types';

export const RESPONSE_LENGTH_DEFAULT_MIN = 1;
export const RESPONSE_LENGTH_DEFAULT_MAX = 500;
export const RESPONSE_LENGTH_MAX_ALLOWED = 5000;

export const VW_STANDARD_STEM = 'At what price point would [product]…';
export const VW_STANDARD_PROMPTS = [
  '…to be priced so low that you would question the quality of the product?',
  '…to be a bargain, a great buy for the money?',
  "…to be starting to get expensive, so that it's not out of the question, but you would have to give some thought to buying it?",
  '…to be so expensive that you would not consider buying it?',
];

export const DEFAULT_QUESTION_PROPS_BY_TYPE = {
  [QUESTION_TYPES.AI_INTERVIEW.value]: {
    expectedDurationSeconds: 120,
    maxProbes: 2,
    modality: 'video' as const,
    probingEnabled: true,
  },
  [QUESTION_TYPES.CHECKBOX.value]: {
    maxAnswers: undefined,
    randomizeOptions: false,
  },
  [QUESTION_TYPES.CONTSUM.value]: {
    forceNumeric: true,
    maxNumber: 100,
    maxTotal: 100,
    minNumber: 0,
    mustBeMax: true,
    onlyWholeNum: true,
  },
  [QUESTION_TYPES.ESSAY.value]: {
    maxLength: RESPONSE_LENGTH_DEFAULT_MAX,
    minLength: RESPONSE_LENGTH_DEFAULT_MIN,
  },
  [QUESTION_TYPES.MAX_DIFF.value]: {
    bestLabel: 'Best',
    numberOfSets: 5,
    optionsPerSet: 5,
    worstLabel: 'Worst',
  },
  [QUESTION_TYPES.NUMERIC.value]: {
    forceNumeric: true,
    maxNumber: 100,
    minNumber: 0,
    onlyWholeNum: true,
  },
  [QUESTION_TYPES.VAN_WESTENDORP.value]: {
    displayOrder: VW_DISPLAY_ORDERS.LOW_TO_HIGH.value,
    forceCurrency: true,
    maxNumber: 100,
    minNumber: 1,
  },
};

// Question types eligible as a piping source.
export const canPipingResponseTypes = [
  QUESTION_TYPES['TABLE-RADIO'].value,
  QUESTION_TYPES.NUMERIC.value,
  QUESTION_TYPES.RADIO.value,
];

export const NONE_OF_THE_ABOVE = 'None of the above';
export const NONE_OF_THE_ABOVE_EU = 'Keine der genannten';
export const OTHER_PLEASE_SPECIFY = 'Other, please specify';
export const OTHER_PLEASE_SPECIFY_EU = 'Sonstiges, bitte angeben';

export const OTHER_PLEASE_SPECIFY_ALTERNATES = [
  OTHER_PLEASE_SPECIFY.toLowerCase(),
  'other: please specify',
  'other please specify',
  'other (specify)',
];
export const OTHER_PLEASE_SPECIFY_ALTERNATES_EU = [
  OTHER_PLEASE_SPECIFY_EU.toLowerCase(),
  'sonstiges bitte angeben',
];

export const FORMATS = {
  CURRENCY: {
    name: 'Currency',
    value: 'forceCurrency',
  },
  NUMBER: {
    name: 'Number',
    value: 'forceNumeric',
  },
  PERCENT: {
    name: 'Percent',
    value: 'forcePercent',
  },
} as const;

export const ANSWER_MAX_LENGTH = 150;
export const QUESTION_MAX_LENGTH = 1000;

export const QUESTION_CONT_SUM_MAX_OPTIONS = 15;
export const QUESTION_GRID_MAX_COLUMNS = 12;
export const QUESTION_GRID_MAX_ROWS = 12;
export const QUESTION_MAX_DIFF_LABEL_LENGTH = 100;
export const QUESTION_MAX_DIFF_MAX_OPTIONS = 30;
export const QUESTION_MAX_DIFF_MAX_SETS = 15;
export const QUESTION_MAX_DIFF_MIN_OPTIONS = 10;
export const QUESTION_MAX_DIFF_MIN_SETS = 3;
export const QUESTION_RANK_MAX_OPTIONS = 15;
export const QUESTION_TURF_MAX_OPTIONS = 30;

export const VALIDATION_MESSAGES = {
  ANSWER_BLANK_ERROR: 'Answer options cannot be blank.',
  ANSWER_DUPLICATE_ERROR: 'Answer options must be unique.',
  ANSWER_LENGTH_ERROR: `Answer text exceeds the max-length of ${ANSWER_MAX_LENGTH}.`,
  ANSWERS_REQUIRED: 'Answer options are required.',
  CARRY_FORWARD_SOURCE_REQUIRED: 'Select a source question for carry forward.',
  CONTSUM_MAX_OPTIONS_ERROR: `${QUESTION_TYPES.CONTSUM.name} questions are limited to ${QUESTION_CONT_SUM_MAX_OPTIONS} options.`,
  GRID_COLUMN_LENGTH_ERROR: `Column exceeds the max-length of ${ANSWER_MAX_LENGTH}.`,
  GRID_COLUMNS_BLANK_ERROR: 'Columns cannot be blank.',
  GRID_COLUMNS_DUPLICATE_ERROR: 'Column text must be unique.',
  GRID_COLUMNS_REQUIRED: `${QUESTION_TYPES['TABLE-RADIO'].name} questions require at least 1 column.`,
  GRID_MAX_COLUMNS_ERROR: `${QUESTION_TYPES['TABLE-RADIO'].name} questions are limited to ${QUESTION_GRID_MAX_COLUMNS} columns.`,
  GRID_MAX_ROWS_ERROR: `${QUESTION_TYPES['TABLE-RADIO'].name} questions are limited to ${QUESTION_GRID_MAX_ROWS} rows.`,
  GRID_ROW_LENGTH_ERROR: `Row exceeds the max-length of ${ANSWER_MAX_LENGTH}.`,
  GRID_ROWS_BLANK_ERROR: 'Rows cannot be blank.',
  GRID_ROWS_DUPLICATE_ERROR: 'Row text must be unique.',
  GRID_ROWS_REQUIRED: `${QUESTION_TYPES['TABLE-RADIO'].name} questions require at least 1 row.`,
  MAX_DIFF_BEST_LABEL_LENGTH_ERROR: `Best label exceeds the max-length of ${QUESTION_MAX_DIFF_LABEL_LENGTH}.`,
  MAX_DIFF_BEST_LABEL_REQUIRED: 'Best label is required and cannot be blank.',
  MAX_DIFF_MAX_OPTIONS_ERROR: `${QUESTION_TYPES.MAX_DIFF.name} questions are limited to ${QUESTION_MAX_DIFF_MAX_OPTIONS} options.`,
  MAX_DIFF_MIN_OPTIONS_ERROR: `${QUESTION_TYPES.MAX_DIFF.name} questions must have at least ${QUESTION_MAX_DIFF_MIN_OPTIONS} options.`,
  MAX_DIFF_WORST_LABEL_LENGTH_ERROR: `Worst label exceeds the max-length of ${QUESTION_MAX_DIFF_LABEL_LENGTH}.`,
  MAX_DIFF_WORST_LABEL_REQUIRED: 'Worst label is required and cannot be blank.',
  MAX_LENGTH_EXCEEDED_ERROR: 'Maximum length cannot exceed 5000 characters.',
  MAX_LENGTH_MIN_LENGTH_ERROR: 'Maximum length must be larger than minimum length.',
  MAX_PERCENTAGE_ERROR: 'Max percentage cannot be greater than 100.',
  MAX_VALUE_MAX_TOTAL_ERROR: 'Maximum allowed value cannot be greater than max total.',
  MAX_VALUE_MIN_VALUE_ERROR: 'Maximum value must be larger than minimum value.',
  MIN_LENGTH_POSITIVE_ERROR: 'Minimum length must be at least 1.',
  MIN_VALUE_NEGATIVE_ERROR: 'Minimum value cannot be negative.',
  NO_NONE_OF_THE_ABOVE: `Can't have none of the above with this question type.`,
  NO_OTHER_PLEASE_SPECIFY: `Can't have a write-in with this question type.`,
  NO_TYPE_SELECTED: 'Select a question type to continue.',
  PROBE_GUIDANCE_REQUIRED: 'Probe guidance is required and cannot be blank.',
  QUESTION_COST_ERROR: 'The question cost exceeds what is available in the survey.',
  QUESTION_MAX_LENGTH_ERROR: `Question text exceeds the max-length of ${QUESTION_MAX_LENGTH}.`,
  QUESTION_TEXT_REQUIRED: 'Question text is required and cannot be blank.',
  RANK_MAX_OPTIONS_ERROR: `${QUESTION_TYPES['RANK-DRAGDROP'].name} questions are limited to ${QUESTION_RANK_MAX_OPTIONS} options.`,
  TURF_MAX_OPTIONS_ERROR: `${QUESTION_TYPES.TURF.name} questions are limited to ${QUESTION_TURF_MAX_OPTIONS} options.`,
  VW_MIN_PRICE_ERROR: 'Minimum price must be greater than 0.',
  VW_PRICE_RANGE_REQUIRED: 'Minimum and maximum price are required.',
};
